import { VariantProps, cva } from 'class-variance-authority'
import React, { ButtonHTMLAttributes, PropsWithChildren, forwardRef } from 'react'
import { Icon as IconType } from 'react-feather'

import { useStore } from '@frame/lib/stores/useStore'
import { cn } from '@frame/lib/utils/cn'

export const buttonVariants = cva(
  'flex items-center justify-center text-center text-base transition-all focus-visible:outline-none focus-visible:border-electric-650 focus-visible:ring-[6px] focus-visible:ring-electric-650/10 hover:shadow-hover active:shadow-none',
  {
    variants: {
      intent: {
        primary: 'border-electric-650 bg-electric-650 text-white hover:bg-electric-700 focus-visible:bg-electric-700',
        secondary: 'border-moss-650 bg-white text-moss-700 hover:border-moss-700 focus-visible:text-moss-700',
        tertiary:
          'border-electric-650 bg-white text-electric-650 hover:border-electric-700 hover:text-electric-700 focus-visible:text-electric-700',
        error:
          'border-error-600 bg-white text-error-600 hover:border-error-700 hover:text-error-700 focus-visible:text-error-700',
      },
      size: {
        default: 'h-auto space-x-2 border-1.5 rounded-sm px-[1.5rem] py-[0.75rem] font-semibold',
        small: 'h-6 space-x-1 border rounded-[0.375rem] px-4 py-2 text-xs font-medium',
      },
      disabled: {
        true: 'border-electric-350 bg-electric-350 text-white pointer-events-none',
      },
      border: {
        false: 'h-auto border-transparent p-0 hover:border-transparent',
      },
      iconOnly: {
        true: 'flex h-7 w-7 flex-col p-0 focus-visible:bg-transparent focus-visible:border-none',
      },
      fullWidth: {
        true: 'w-full',
      },
      applyCobranding: {
        true: 'bg-cobranding hover:bg-cobranding-hover-dark border-cobranding focus-visible:bg-cobranding-hover-dark',
      },
      showAsDisabled: {
        true: 'cursor-default border-moss-350 bg-moss-350 hover:border-moss-350 hover:bg-moss-350 hover:shadow-none',
      },
    },
    compoundVariants: [
      {
        disabled: true,
        intent: 'primary',
        className: 'border-electric-350 bg-electric-350 text-white',
      },
      {
        disabled: true,
        intent: 'secondary',
        className: 'border-moss-350 bg-moss-100 text-moss-400',
      },
      {
        disabled: true,
        intent: 'tertiary',
        className: 'border-electric-350 bg-moss-100 text-electric-350',
      },
      {
        disabled: true,
        intent: 'error',
        className: 'bg-moss-100 border-error-350 text-error-350',
      },
      {
        border: false,
        intent: 'primary',
        className: 'bg-transparent text-electric-650 hover:text-electric-700 hover:bg-transparent hover:shadow-none',
      },
      {
        border: false,
        intent: 'secondary',
        className: 'bg-transparent text-moss-650 hover:text-moss-700 hover:bg-transparent hover:shadow-none',
      },
      {
        disabled: true,
        border: false,
        intent: 'primary',
        className: 'text-electric-350',
      },
      {
        disabled: true,
        border: false,
        intent: 'secondary',
        className: 'text-moss-400',
      },
      {
        disabled: true,
        border: [true, false],
        intent: 'primary',
        applyCobranding: true,
        className: 'bg-cobranding opacity-70 border-cobranding border-opacity-70',
      },
      {
        border: false,
        iconOnly: true,
        className: 'w-auto h-auto bg-transparent hover:bg-transparent',
      },
      {
        border: true,
        iconOnly: true,
        className: 'w-7 h-7',
      },
      {
        border: false,
        intent: 'tertiary',
        className: 'hover:shadow-none',
      },
      {
        border: false,
        disabled: true,
        className: 'border-transparent',
      },
      {
        disabled: false,
        border: [true, false],
        iconOnly: true,
        intent: 'tertiary',
        applyCobranding: true,
        className: 'bg-white border-cobranding opacity-80 text-cobranding',
      },
      {
        intent: 'secondary',
        applyCobranding: true,
        className: 'bg-white transition-all hover:bg-white border-moss-650 hover:border-moss-700',
      },
      {
        intent: 'tertiary',
        applyCobranding: true,
        className:
          'bg-white border-cobranding text-cobranding transition-all hover:bg-white hover:text-cobranding-hover-dark hover:border-cobranding-hover-dark focus-visible:text-cobranding-hover-dark',
      },
      {
        showAsDisabled: true,
        disabled: true,
        className:
          'cursor-default border-moss-350 bg-moss-350 hover:border-moss-350 hover:bg-moss-350 hover:shadow-none',
      },
    ],
    defaultVariants: {
      intent: 'primary',
      size: 'default',
      disabled: false,
      border: true,
      fullWidth: true,
      applyCobranding: false,
      showAsDisabled: false,
    },
  }
)

type Props = {
  intent?: VariantProps<typeof buttonVariants>['intent']
  size?: VariantProps<typeof buttonVariants>['size']
  disabled?: VariantProps<typeof buttonVariants>['disabled']
  border?: VariantProps<typeof buttonVariants>['border']
  fullWidth?: boolean
  Icon?: IconType
  applyCobranding?: boolean
  ariaLabel?: string
  showAsDisabled?: boolean
}

export const Button = forwardRef<HTMLButtonElement, PropsWithChildren<Props & ButtonHTMLAttributes<HTMLButtonElement>>>(
  (
    {
      intent,
      size,
      disabled,
      border,
      fullWidth,
      Icon,
      applyCobranding,
      children,
      className,
      ariaLabel,
      showAsDisabled,
      ...rest
    },
    ref
  ) => {
    const iconOnly = Icon && !children
    const cobrandingColor = useStore((state) => state.session?.configuration.cobranding.button_color)

    function getIconSize() {
      if (iconOnly) return '24px'
      if (size === 'small') return '12px'
      return '16px'
    }

    return (
      <button
        aria-label={ariaLabel}
        ref={ref}
        type="button"
        className={cn(
          buttonVariants({
            intent,
            size,
            disabled,
            border,
            iconOnly,
            fullWidth,
            applyCobranding: applyCobranding && Boolean(cobrandingColor),
            showAsDisabled,
          }),
          className
        )}
        disabled={disabled}
        {...rest}
      >
        {Icon && <Icon size={getIconSize()} />}
        <span>{children}</span>
      </button>
    )
  }
)

Button.displayName = 'Button'
